import {
  ProjectManagementConfigTier,
  ProjectManagementConfigTierUpdate,
  RiskDateFieldRef
} from '../../../Api/Admin/ProjectManagementConfigs/projectManagementConfigsApi';

export const DEFAULT_PRIMARY_DATE_FIELD = 'estimatedDeliveryDate';
export const DEFAULT_COMPARISON_DATE_FIELD = 'customerRequestedDate';

// An API predating the pair sends no labels, so the defaults have to name themselves.
const DEFAULT_DATE_FIELD_LABELS: Record<string, string> = {
  [DEFAULT_PRIMARY_DATE_FIELD]: 'Estimated Delivery Date',
  [DEFAULT_COMPARISON_DATE_FIELD]: 'Customer Requested Date'
};

export interface TierDatePair {
  primaryDateField: string;
  comparisonDateField: string;
}

export interface TierDateSide {
  // Names a selection the catalogue doesn't list: withdrawn, or not loaded.
  orphanLabel?: string;
  unavailable: boolean;
}

export interface TierDateSides {
  primary: TierDateSide;
  comparison: TierDateSide;
}

type TierDateUpdate = Pick<ProjectManagementConfigTierUpdate, 'primary_date_field' | 'comparison_date_field'>;

// Only while the stored date is still selected; a replacement is named by the catalogue.
const sideFor = (stored: RiskDateFieldRef | undefined, selected: string): TierDateSide =>
  stored && stored.id === selected
    ? { orphanLabel: stored.label, unavailable: !stored.available }
    : { orphanLabel: DEFAULT_DATE_FIELD_LABELS[selected], unavailable: false };

export const tierDateSides = (pair: TierDatePair, stored?: ProjectManagementConfigTier): TierDateSides => ({
  primary: sideFor(stored?.primaryDateField, pair.primaryDateField),
  comparison: sideFor(stored?.comparisonDateField, pair.comparisonDateField)
});

export const hasUnavailableDate = (sides: TierDateSides) => sides.primary.unavailable || sides.comparison.unavailable;

// The API refuses a date measured against itself.
export const measuresOneDateAgainstItself = (pair: TierDatePair) => pair.primaryDateField === pair.comparisonDateField;

// An omitted key keeps the stored date, except when inherited: the new row takes the column default.
export const changedDates = (
  pair: TierDatePair,
  stored?: ProjectManagementConfigTier,
  { inherited = false }: { inherited?: boolean } = {}
): TierDateUpdate => ({
  ...((inherited || pair.primaryDateField !== stored?.primaryDateField?.id) && {
    primary_date_field: pair.primaryDateField
  }),
  ...((inherited || pair.comparisonDateField !== stored?.comparisonDateField?.id) && {
    comparison_date_field: pair.comparisonDateField
  })
});
